import { TFunction } from "i18next";
import type { DoctorInvoiceBreakdown, Recipient } from "src/types";

export interface InvoiceDeleteBreakdown {
    total: number;
    open: number;
    sent: number;
    notSent: number;
    m1: number;
    m2: number;
    m3: number;
    legal: number;
}

type InvoiceLike = {
    paidAmount?: number;
    amount?: number;
    sentDate?: string | null;
    dunning?: number;
};

export function computeInvoiceDeleteBreakdown(invoices: InvoiceLike[]): InvoiceDeleteBreakdown | null {
    if (invoices.length === 0) return null;
    const isOpen = (inv: InvoiceLike) => (inv.paidAmount ?? 0) < (inv.amount ?? 0);
    const openInvoices = invoices.filter(isOpen);
    const openSent = openInvoices.filter((inv) => !!inv.sentDate);
    const notSent = invoices.filter((inv) => !inv.sentDate).length;
    return {
        total: invoices.length,
        open: openInvoices.length,
        sent: invoices.length - notSent,
        notSent,
        m1: openSent.filter((inv) => inv.dunning === 1).length,
        m2: openSent.filter((inv) => inv.dunning === 2).length,
        m3: openSent.filter((inv) => inv.dunning === 3).length,
        legal: openSent.filter((inv) => (inv.dunning ?? 0) >= 4).length,
    };
}

export function breakdownFromRecipient(r: Recipient): InvoiceDeleteBreakdown | null {
    const total = (r.invoicesDone ?? 0) + (r.invoicesOpen ?? 0);
    if (total <= 0) return null;
    const notSent = r.invoicesNotSent ?? 0;
    return {
        total,
        open: r.invoicesOpen ?? 0,
        sent: total - notSent,
        notSent,
        m1: r.invoicesM1 ?? 0,
        m2: r.invoicesM2 ?? 0,
        m3: r.invoicesM3 ?? 0,
        legal: r.invoicesLegal ?? 0,
    };
}

export function sumInvoiceDeleteBreakdowns(items: InvoiceDeleteBreakdown[]): InvoiceDeleteBreakdown | null {
    if (items.length === 0) return null;
    return items.reduce(
        (acc, b) => ({
            total: acc.total + b.total,
            open: acc.open + b.open,
            sent: acc.sent + b.sent,
            notSent: acc.notSent + b.notSent,
            m1: acc.m1 + b.m1,
            m2: acc.m2 + b.m2,
            m3: acc.m3 + b.m3,
            legal: acc.legal + b.legal,
        }),
        { total: 0, open: 0, sent: 0, notSent: 0, m1: 0, m2: 0, m3: 0, legal: 0 },
    );
}

/** Warns that a delete cascades to invoices owned by doctors other than the acting one —
 *  only meaningful once more than one distinct doctor is involved (patients are shared
 *  practice-wide, but invoices stay per-doctor). Names each doctor with how many of their
 *  invoices would be removed. */
export function formatOtherDoctorsWarning(t: TFunction, breakdown: DoctorInvoiceBreakdown[]): string | null {
    if (breakdown.length <= 1) return null;
    const doctors = breakdown
        .map((d) => t("common.doctorInvoiceCount", { count: d.count, name: d.name }))
        .join(", ");
    return t("common.deleteAlsoOtherDoctorsInvoices", { doctors });
}

/** Merges per-doctor breakdowns from multiple patients/recipients, summing counts for
 *  doctors that appear in more than one (e.g. a bulk delete spanning several patients). */
export function mergeDoctorInvoiceBreakdowns(items: DoctorInvoiceBreakdown[][]): DoctorInvoiceBreakdown[] {
    const countByName = new Map<string, number>();
    for (const breakdown of items) {
        for (const d of breakdown) {
            countByName.set(d.name, (countByName.get(d.name) ?? 0) + d.count);
        }
    }
    return [...countByName.entries()].map(([name, count]) => ({ name, count }));
}

/** Headline: total + open count. */
export function formatDeleteInvoicesHeadline(t: TFunction, total: number, open: number): string | null {
    if (total <= 0) return null;
    const openCount = Math.min(Math.max(0, open), total);
    if (total === 1) {
        return openCount >= 1
            ? t("recipients.deleteAlsoInvoices_one_open")
            : t("recipients.deleteAlsoInvoices_one_paid");
    }
    if (openCount === 0) {
        return t("recipients.deleteAlsoInvoices_allPaid", { count: total });
    }
    return t("recipients.deleteAlsoInvoices_withOpen", { count: total, open: openCount });
}
